import { isValidPersonName } from "@bavard/utils";
import { z } from "zod";

/**
 * Strict person-name schema — the api-client mirror of the server domain's
 * `StrictPersonNameSchema`.
 *
 * Delegates to the shared `@bavard/utils` `isValidPersonName` predicate (the
 * same rule the data grid enforces via `validateStrictName`) so AI-tool write
 * inputs reject non-names like ".", "..", digits, or punctuation-only strings.
 * The reserved literal "SYSTEM" stays valid.
 *
 * `.refine()` does NOT serialize into the JSON Schema sent to the model, so this
 * keeps the model-facing contract identical to the domain copy (drift-guard
 * safe) while validating at parse time.
 */
export const PersonNameStrictSchema = z
	.string()
	.min(1)
	.refine(isValidPersonName, {
		message:
			"Must be a real person name (letters only, at least two words, no digits or punctuation-only values).",
	});

/**
 * Optional strict person-name — the api-client mirror of the server domain's
 * `OptionalStrictPersonNameSchema`. LLMs — especially under strict-JSON-schema
 * providers that force every property to be present — often express "not
 * applicable" as `null`, `""`, or `" "` instead of omitting the field. All of
 * those normalize to `undefined` BEFORE the strict refine runs, so a
 * placeholder can never fail validation; only real junk still errors, and that
 * error is fed back to the model as a tool error it can retry on.
 */
export const PersonNameStrictOptionalSchema = z
	.string()
	.nullish()
	.transform((value) => {
		const trimmed = value?.trim();
		return trimmed ? trimmed : undefined;
	})
	.pipe(PersonNameStrictSchema.optional());

/**
 * Convenience predicate re-export so callers can validate a name without
 * pulling in the full schema.
 */
export { isValidPersonName } from "@bavard/utils";
